import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Grid,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import Tooltip from "@mui/material/Tooltip";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const STORAGE_KEY = "chatio_saved_news";

const fetchNewsCallable = httpsCallable(functions, "fetchNews");

function getSaved() {
  return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
}

function toggleSaveArticle(article, countryLabel) {
  const saved = getSaved();
  const exists = saved.find((a) => a.url === article.url);
  let updated;
  if (exists) {
    updated = saved.filter((a) => a.url !== article.url);
  } else {
    updated = [{ ...article, countryLabel }, ...saved];
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return !exists;
}

const COUNTRIES = [
  { code: "us", label: "🇺🇸 EE.UU.", lang: "en", region: "América del Norte", searchTerm: "United States" },
  { code: "ca", label: "🇨🇦 Canadá", lang: "en", region: "América del Norte", searchTerm: "Canada" },
  { code: "mx", label: "🇲🇽 México", lang: "es", region: "América Latina", searchTerm: "México" },
  { code: "ar", label: "🇦🇷 Argentina", lang: "es", region: "América Latina", searchTerm: "Argentina" },
  { code: "br", label: "🇧🇷 Brasil", lang: "pt", region: "América Latina", searchTerm: "Brasil" },
  { code: "cl", label: "🇨🇱 Chile", lang: "es", region: "América Latina", searchTerm: "Chile" },
  { code: "co", label: "🇨🇴 Colombia", lang: "es", region: "América Latina", searchTerm: "Colombia" },
  { code: "gb", label: "🇬🇧 Reino Unido", lang: "en", region: "Europa", searchTerm: "United Kingdom" },
  { code: "es", label: "🇪🇸 España", lang: "es", region: "Europa", searchTerm: "España" },
  { code: "de", label: "🇩🇪 Alemania", lang: "de", region: "Europa", searchTerm: "Deutschland" },
  { code: "fr", label: "🇫🇷 Francia", lang: "fr", region: "Europa", searchTerm: "France" },
  { code: "it", label: "🇮🇹 Italia", lang: "it", region: "Europa", searchTerm: "Italia" },
  { code: "ru", label: "🇷🇺 Rusia", lang: "ru", region: "Europa", searchTerm: "Россия" },
  { code: "in", label: "🇮🇳 India", lang: "en", region: "Asia", searchTerm: "India" },
  { code: "cn", label: "🇨🇳 China", lang: "zh", region: "Asia", searchTerm: "中国" },
  { code: "jp", label: "🇯🇵 Japón", lang: "ja", region: "Asia", searchTerm: "日本" },
  { code: "kr", label: "🇰🇷 Corea del Sur", lang: "ko", region: "Asia", searchTerm: "한국" },
  { code: "ae", label: "🇦🇪 Emiratos Árabes", lang: "ar", region: "Medio Oriente", searchTerm: "الإمارات" },
  { code: "il", label: "🇮🇱 Israel", lang: "he", region: "Medio Oriente", searchTerm: "ישראל" },
  { code: "za", label: "🇿🇦 Sudáfrica", lang: "en", region: "África", searchTerm: "South Africa" },
  { code: "ng", label: "🇳🇬 Nigeria", lang: "en", region: "África", searchTerm: "Nigeria" },
  { code: "eg", label: "🇪🇬 Egipto", lang: "ar", region: "África", searchTerm: "مصر" },
  { code: "au", label: "🇦🇺 Australia", lang: "en", region: "Oceanía", searchTerm: "Australia" },
];

const CATEGORIES = [
  { code: "general", label: "General" },
  { code: "world", label: "Mundo" },
  { code: "nation", label: "Nación" },
  { code: "business", label: "Negocios" },
  { code: "technology", label: "Tecnología" },
  { code: "entertainment", label: "Entretenimiento" },
  { code: "sports", label: "Deportes" },
  { code: "science", label: "Ciencia" },
  { code: "health", label: "Salud" },
];

// Group countries by region for display
const REGIONS = [...new Set(COUNTRIES.map((c) => c.region))];

function News() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [country, setCountry] = useState("us");
  const [category, setCategory] = useState("general");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [savedUrls, setSavedUrls] = useState(() => getSaved().map((a) => a.url));

  const handleToggleSave = (article) => {
    const label = COUNTRIES.find((c) => c.code === country)?.label || "";
    const isNowSaved = toggleSaveArticle(article, label);
    setSavedUrls(getSaved().map((a) => a.url));
    return isNowSaved;
  };

  const selectedCountry = COUNTRIES.find((c) => c.code === country);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    setArticles([]);
    try {
      const lang = selectedCountry?.lang || "en";
      const result = await fetchNewsCallable({
        country: selectedCountry?.searchTerm || country,
        category,
        search: search.trim(),
        lang,
      });
      setArticles(result.data.articles || []);
    } catch (err) {
      console.error("GNews error:", err);
      setError(
        err?.message?.includes("UNAVAILABLE") || err?.message?.includes("unavailable")
          ? "No se pudo conectar con el servicio de noticias. Intentá de nuevo."
          : err.message || "No se pudieron cargar las noticias."
      );
    } finally {
      setLoading(false);
    }
  }, [country, category, search, selectedCountry]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <Container sx={{ mt: 16, mb: 12 }}>
      {/* Search bar */}
      <Box
        component="form"
        onSubmit={handleSearch}
        sx={{ mb: 2 }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar noticias..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                  }}
                >
                  ✕
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Región / País</InputLabel>
          <Select
            value={country}
            label="Región / País"
            onChange={(e) => setCountry(e.target.value)}
          >
            {REGIONS.map((region) => [
              <MenuItem
                key={`region-${region}`}
                disabled
                sx={{ fontWeight: "bold", fontSize: 12, color: "#1976d2", opacity: "1 !important" }}
              >
                — {region} —
              </MenuItem>,
              ...COUNTRIES.filter((c) => c.region === region).map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.label}
                </MenuItem>
              )),
            ])}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Categoría</InputLabel>
          <Select
            value={category}
            label="Categoría"
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <IconButton onClick={fetchNews} color="primary" title="Actualizar noticias">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Region label */}
      {selectedCountry && !search && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <PublicIcon fontSize="small" color="primary" />
          <Typography variant="body2" color="text.secondary">
            Mostrando noticias de{" "}
            <strong>{selectedCountry.label}</strong> — {selectedCountry.region}
          </Typography>
        </Box>
      )}
      {search && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Resultados para: <strong>&quot;{search}&quot;</strong>
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Box
          sx={{
            p: 2,
            bgcolor: "#fff3f3",
            border: "1px solid #f44336",
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Articles */}
      {!loading && (
        <Grid container spacing={2}>
          {articles.map((article, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  "&:hover": { elevation: 6, transform: "translateY(-2px)" },
                  transition: "transform 0.15s ease",
                }}
              >
                <CardActionArea
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                >
                  {article.image && (
                    <CardMedia
                      component="img"
                      height="180"
                      image={article.image}
                      alt={article.title}
                      sx={{ objectFit: "cover" }}
                    />
                  )}
                  <CardContent sx={{ flex: 1 }}>
                    {/* Source + region */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <PublicIcon sx={{ fontSize: 14, color: "#888" }} />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                        {article.source?.name}
                      </Typography>
                      <Chip
                        label={selectedCountry?.label}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: "auto", fontSize: 10, height: 20 }}
                      />
                    </Box>

                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ lineHeight: 1.4 }}
                    >
                      {article.title}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {article.description}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {article.publishedAt
                        ? new Date(article.publishedAt).toLocaleDateString(
                            "es-ES",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : ""}
                    </Typography>
                  </CardContent>
                </CardActionArea>

                {/* Bookmark button */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 0.5 }}>
                  <Tooltip title={savedUrls.includes(article.url) ? "Quitar guardado" : "Guardar noticia"}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleToggleSave(article)}
                    >
                      {savedUrls.includes(article.url) ? (
                        <BookmarkIcon fontSize="small" />
                      ) : (
                        <BookmarkBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}

          {!loading && articles.length === 0 && !error && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ mt: 6 }}>
                No hay noticias disponibles para esta selección.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
}

export default News;
